import {useState, useEffect, useMemo} from 'react'
import './style.css'
import DataTable from 'react-data-table-component'
import axios from 'axios'
import {useLocation} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useDispatch} from 'react-redux'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {matchConsumerId, getToday} from '../../utility'
import * as auth from '../../../../app/modules/auth/redux/AuthRedux'

import CsvDownload from 'react-json-to-csv'
import 'react-confirm-alert/src/react-confirm-alert.css'
import 'react-datepicker/dist/react-datepicker.css'
import DatePicker from 'react-datepicker'

export function MeetingUsageReport(): JSX.Element {
  const [data, setData] = useState([])
  const [allData, setAllData] = useState([]) // For CSV export - fetched separately
  const [pending, setPending] = useState(true)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [dates, setDates] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  })
  
  const user: any = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any
  const query = useLocation()
  const dispatch = useDispatch()
  const location = query.search || ''
  const consumerId = location.split('=')[1]

  const {
    user: {id: userId},
  } = user

  // Format dates to 'YYYY-MM-DD' for API call
  const startDateFormatted = dates.startDate.toISOString().split('T')[0]
  const endDateFormatted = dates.endDate.toISOString().split('T')[0]

  useEffect(() => {
    console.log({userId})
    if (userId) {
      matchConsumerId(userId, dispatch, auth)
    }
  }, [userId])

  // Fetch data when dates change - reset to page 1
  useEffect(() => {
    if (consumerId && dates?.endDate && dates?.startDate) {
      setCurrentPage(1) // Reset to first page when dates change
      getMeetingUsageData(1, perPage)
    }
  }, [consumerId, dates.startDate, dates.endDate])

  // Fetch data when page or perPage changes
  useEffect(() => {
    if (consumerId && dates?.endDate && dates?.startDate) {
      getMeetingUsageData(currentPage, perPage)
    }
  }, [currentPage, perPage])

  const getMeetingUsageData = async (page: number, limit: number) => {
    setPending(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL_BACKEND_LOCAL}/meeting-usage-report/${consumerId}?startDate=${startDateFormatted}&endDate=${endDateFormatted}&page=${page}&limit=${limit}`
      )
      setData(response.data.data)
      setTotalRows(response.data.totalFiltered) // Use filtered count for pagination
    } catch (error) {
      console.error('Error fetching meeting usage data:', error)
      setData([])
      setTotalRows(0)
    } finally {
      setPending(false)
    }
  }

  // Fetch all data for CSV export (only when export button is clicked)
  const fetchAllDataForExport = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL_BACKEND_LOCAL}/meeting-usage-report/${consumerId}?startDate=${startDateFormatted}&endDate=${endDateFormatted}&page=1&limit=10000`
      )
      return response.data.data
    } catch (error) {
      console.error('Error fetching all data for export:', error)
      return []
    }
  }

  const handleExportClick = async () => {
    const allDataForExport = await fetchAllDataForExport()
    setAllData(allDataForExport)
    // Trigger download after data is loaded
    setTimeout(() => {
      const hiddenCsvDownload = document.querySelector('.export-csv-hidden') as HTMLElement;
      if (hiddenCsvDownload) {
        hiddenCsvDownload.click();
      }
    }, 100)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    setCurrentPage(page)
  }

  const filterAndMap = (details) => {
    return (
      details &&
      details.length > 0 &&
      details.map((item) => {
        let obj = {
          ['Created At']: item.createdAt,
          ['Time Zone']: item.displayTimeZone,
          ['Room Name']: item.roomName,
          Domain: item.domain,
          ['Participant Count']: item.participantCount,
          ['Meeting Minutes']: item.billableMinutes,
          ['Participant Minutes']: item.participantMinutes,
          ['Dynamic Participant Minutes']: item.calcParticipantMinutes,
          ['Savings']: item.savings,
          ['Recording']: item.recordingOn === true ? 'Yes' : 'No',
          ['Transcription']: item.transcriptionOn === true ? 'Yes' : 'No',
          ['Participants']: item.participantList,
        }
        return obj
      })
    );
  }
  
  const columns = useMemo(
    () => [
      {
        name: 'Time (UTC)',
        selector: (row: any) => row.createdAt,
        sortable: true,
      },
      {
        name: 'Room',
        selector: (row: any) => row.roomName,
        sortable: true,
      },
      {
        name: 'Part. Count',
        selector: (row: any) => row.participantCount,
        sortable: true,
      },
      {
        name: 'Part. Min.',
        selector: (row: any) => row.calcParticipantMinutes,
        sortable: true,
      },
      {
        name: 'Dynamic Min.',
        selector: (row: any) => row.participantMinutes,
        sortable: true,
      },
      {
        name: 'Savings',
        selector: (row: any) => `${row.savings}%`,
        sortable: true,
      },
      {
        name: 'Recording',
        selector: (row: any) => (row.recordingOn === true ? 'Yes' : 'No'),
        sortable: true,
      },
      {
        name: 'Participants',
        selector: (row: any) => row.participantList,
        sortable: true,
        grow: 2,
      },
    ],
    []
  )

  const labelStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '1rem',
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Meetings</PageTitle>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', width: '70%'}}>
          <label style={labelStyles}>Start date</label>
          <DatePicker
            selected={dates.startDate}
            onChange={(date) => {
              const selectedDate = date as Date;
              const today = new Date();
              if (selectedDate instanceof Date && !isNaN(selectedDate.getTime()) && !(selectedDate > today)) {
                setDates({
                  ...dates,
                  startDate: selectedDate,
                });
              } else {
                console.log('Invalid start date selected.');
              }
            }}
            className='form-control form-control-solid w-200px ps-3'
            wrapperClassName='date-picker-input me-4'
            maxDate={new Date()}
          />
          <label style={{...labelStyles, marginLeft: '30px', paddingRight: '0.5rem'}}>End date</label>
          <DatePicker
            selected={dates.endDate}
            onChange={(date) => {
              const selectedDate = date as Date;
              const today = new Date();
              if (selectedDate instanceof Date && !isNaN(selectedDate.getTime()) && selectedDate <= today) {
                if (selectedDate < dates.startDate) {
                  console.log('End date cannot be before start date.');
                } else {
                  setDates({...dates, endDate: selectedDate});
                }
              } else {
                console.log('Invalid end date selected.');
              }
            }}
            className='form-control form-control-solid w-200px ps-3'
            wrapperClassName='date-picker-input me-4'
            maxDate={new Date()}
          />
        </div>
        <div className='d-flex align-items-center'>
          <button
            onClick={handleExportClick}
            className='btn secondary-button btn-sm export-csv'
            disabled={totalRows === 0}
          >
            Export to CSV
          </button>
          {/* Hidden CSV download component */}
          <div style={{display: 'none'}}>
            <CsvDownload
              data={filterAndMap(allData)}
              filename={`${new Date().toISOString().slice(0, 10)}-meeting-usage-report.csv`}
              className='export-csv-hidden'
            >
              Export
            </CsvDownload>
          </div>
        </div>
      </div>

      <DataTable
        title={<h4 style={{marginTop: '40px', marginBottom: '40px'}}>Meetings</h4>}
        data={data}
        columns={columns}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        progressPending={pending}
        progressComponent={
          <div style={{fontSize: '1.35rem'}}>
            <div className='d-flex justify-content-center mb-5'>
              <div className='spinner-border text-primary'>
                <span className='sr-only'>Please wait...</span>
              </div>
            </div>
          </div>
        }
        customStyles={{
          table: {
            style: {
              paddingLeft: '20px',
              paddingRight: '20px',
            },
          },
          headRow: {
            style: {
              backgroundColor: '#f0f0f0',
              minHeight: '40px',
            },
          },
          headCells: {
            style: {
              fontSize: '1rem',
              color: '#495057',
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
          cells: {
            style: {
              paddingLeft: '5px',
              paddingRight: '5px',
              fontSize: '0.95rem',
            },
          },
          rows: {
            style: {
              '&:nth-child(even)': {
                backgroundColor: '#f8f9fa',
              },
              minHeight: '45px',
            },
          },
          pagination: {
            style: {
              backgroundColor: '#ffffff',
              color: '#333',
            },
          },
        }}
      />
    </>
  )
}
